import {
    Braces,
    ClipboardCopy,
    Download,
    Search,
    WrapText,
} from 'lucide-react';
import { type ReactNode, useCallback, useMemo, useState } from 'react';

import { Button } from '@apps/web/components/ui/button';
import { Input } from '@apps/web/components/ui/input';
import { cn } from '@apps/web/lib/utils';
import {
    copyToClipboard,
    formatPayloadBytes,
    getUtf8ByteSize,
    stringifyPayload,
} from '@apps/web/routes/_app/admin/agent/payload_viewer_shared';

const DEFAULT_JSON_PAYLOAD_VIEW_MAX_CHARS = 120_000;
const DEFAULT_JSON_PAYLOAD_VIEW_MAX_LINES = 4_000;
const JSON_TOKEN_PATTERN =
    /("(?:\\.|[^"\\])*"|[-]?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?|\btrue\b|\bfalse\b|\bnull\b)/g;

type PreparedPayloadDocument = {
    readonly fullText: string;
    readonly visibleText: string;
    readonly totalChars: number;
    readonly totalBytes: number;
    readonly visibleChars: number;
    readonly lineCount: number;
    readonly format: 'json' | 'text';
    readonly truncated: boolean;
};

function preparePayloadDocument({
    maxVisibleChars,
    maxVisibleLines,
    value,
}: {
    readonly maxVisibleChars: number;
    readonly maxVisibleLines: number;
    readonly value: unknown;
}): PreparedPayloadDocument {
    const { text, format: payloadFormat } = stringifyPayload(value);
    const totalBytes = getUtf8ByteSize(text);
    let visibleText = text;
    let truncated = false;

    if (visibleText.length > maxVisibleChars) {
        visibleText = visibleText.slice(0, maxVisibleChars);
        truncated = true;
    }

    const lines = visibleText.split('\n');
    if (lines.length > maxVisibleLines) {
        visibleText = lines.slice(0, maxVisibleLines).join('\n');
        truncated = true;
    }

    return {
        fullText: text,
        visibleText,
        totalChars: text.length,
        totalBytes,
        visibleChars: visibleText.length,
        lineCount: visibleText.split('\n').length,
        format: payloadFormat,
        truncated,
    };
}

function countSearchMatches(text: string, query: string): number | null {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) {
        return null;
    }

    const normalizedText = text.toLowerCase();
    let count = 0;
    let offset = 0;
    while (offset < normalizedText.length) {
        const matchIndex = normalizedText.indexOf(normalizedQuery, offset);
        if (matchIndex === -1) {
            break;
        }
        count += 1;
        offset = matchIndex + normalizedQuery.length;
    }
    return count;
}

function highlightSearchSegments(line: string, query: string): ReactNode {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) {
        return line;
    }

    const normalizedLine = line.toLowerCase();
    const segments: Array<ReactNode> = [];
    let offset = 0;
    let key = 0;
    while (offset < line.length) {
        const matchIndex = normalizedLine.indexOf(normalizedQuery, offset);
        if (matchIndex === -1) {
            segments.push(line.slice(offset));
            break;
        }
        if (matchIndex > offset) {
            segments.push(line.slice(offset, matchIndex));
        }
        segments.push(
            <mark
                key={`match-${key}`}
                className="rounded-[2px] bg-[var(--aa-warning-soft)] px-0.5 text-[var(--aa-ink)]"
            >
                {line.slice(matchIndex, matchIndex + normalizedQuery.length)}
            </mark>,
        );
        key += 1;
        offset = matchIndex + normalizedQuery.length;
    }
    return segments;
}

function renderJsonSyntaxLine(line: string): ReactNode {
    const segments: Array<ReactNode> = [];
    let cursor = 0;
    let segmentIndex = 0;
    for (const match of line.matchAll(JSON_TOKEN_PATTERN)) {
        const token = match[0];
        const matchIndex = match.index ?? 0;
        if (matchIndex > cursor) {
            segments.push(line.slice(cursor, matchIndex));
        }

        const afterToken = line.slice(matchIndex + token.length);
        const isKey = token.startsWith('"') && /^\s*:/.test(afterToken);
        // Syntax colors stay inside the theme tokens: purple keys carry
        // the brand, strings/numbers/booleans reuse the status hues.
        const className = isKey
            ? 'font-medium text-[var(--aa-purple-strong)]'
            : token.startsWith('"')
              ? 'text-[var(--aa-success)]'
              : token === 'true' || token === 'false'
                ? 'font-medium text-[var(--aa-warning)]'
                : token === 'null'
                  ? 'text-[var(--aa-ink-faint)]'
                  : 'text-[var(--aa-info)]';

        segments.push(
            <span key={`token-${segmentIndex}`} className={className}>
                {token}
            </span>,
        );
        segmentIndex += 1;
        cursor = matchIndex + token.length;
    }

    if (cursor < line.length) {
        segments.push(line.slice(cursor));
    }

    return segments.length > 0 ? segments : line;
}

function renderPayloadLine(line: string, searchQuery: string): ReactNode {
    if (searchQuery.trim()) {
        return highlightSearchSegments(line, searchQuery);
    }
    return renderJsonSyntaxLine(line);
}

function downloadTextFile({
    filename,
    text,
}: {
    readonly filename: string;
    readonly text: string;
}): void {
    if (typeof document === 'undefined') {
        return;
    }

    const blob = new Blob([text], {
        type: 'application/json;charset=utf-8',
    });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    anchor.rel = 'noopener';
    anchor.style.display = 'none';
    document.body.appendChild(anchor);
    anchor.dispatchEvent(
        new MouseEvent('click', {
            bubbles: true,
            cancelable: true,
            view: window,
        }),
    );
    window.setTimeout(() => {
        anchor.remove();
        URL.revokeObjectURL(url);
    }, 1_000);
}

export function JsonPayloadViewer({
    value,
    title,
    filename = 'admin-agent-payload.json',
    maxVisibleChars = DEFAULT_JSON_PAYLOAD_VIEW_MAX_CHARS,
    maxVisibleLines = DEFAULT_JSON_PAYLOAD_VIEW_MAX_LINES,
    className,
    defaultWrap = false,
}: {
    readonly value: unknown;
    readonly title: string;
    readonly filename?: string;
    readonly maxVisibleChars?: number;
    readonly maxVisibleLines?: number;
    readonly className?: string;
    readonly defaultWrap?: boolean;
}) {
    const [searchQuery, setSearchQuery] = useState('');
    const [wrapLines, setWrapLines] = useState(defaultWrap);
    const documentState = useMemo(
        () =>
            preparePayloadDocument({
                maxVisibleChars,
                maxVisibleLines,
                value,
            }),
        [maxVisibleChars, maxVisibleLines, value],
    );
    const lines = useMemo(
        () => documentState.visibleText.split('\n'),
        [documentState.visibleText],
    );
    const visibleMatchCount = useMemo(
        () => countSearchMatches(documentState.visibleText, searchQuery),
        [documentState.visibleText, searchQuery],
    );
    const fullMatchCount = useMemo(
        () => countSearchMatches(documentState.fullText, searchQuery),
        [documentState.fullText, searchQuery],
    );
    const copyPayload = useCallback(() => {
        copyToClipboard(title, documentState.fullText);
    }, [documentState.fullText, title]);
    const downloadPayload = useCallback(() => {
        downloadTextFile({
            filename,
            text: documentState.fullText,
        });
    }, [documentState.fullText, filename]);
    const payloadSummary = [
        documentState.format.toUpperCase(),
        formatPayloadBytes(documentState.totalBytes),
        `${documentState.lineCount.toLocaleString()} line${
            documentState.lineCount === 1 ? '' : 's'
        }`,
        fullMatchCount !== null
            ? documentState.truncated && visibleMatchCount !== fullMatchCount
                ? `${fullMatchCount.toLocaleString()} matches, ${
                      visibleMatchCount ?? 0
                  } visible`
                : `${fullMatchCount.toLocaleString()} match${
                      fullMatchCount === 1 ? '' : 'es'
                  }`
            : null,
    ].filter((value): value is string => value !== null);

    return (
        <div
            className={cn(
                'flex min-h-0 flex-col overflow-hidden rounded-[2px] border border-[var(--aa-border)] bg-[var(--aa-surface)]',
                className,
            )}
            data-testid="json-payload-viewer"
        >
            <div className="flex h-10 shrink-0 items-center gap-2 border-b border-[var(--aa-border)] bg-[var(--aa-surface)] px-3">
                <div className="flex min-w-0 flex-1 items-center gap-2">
                    <Braces className="size-3.5 shrink-0 text-[var(--aa-ink-faint)]" />
                    <span className="aa-title shrink-0">{title}</span>
                    <span className="min-w-0 truncate font-mono text-[10px] text-[var(--aa-ink-faint)] tabular-nums">
                        {payloadSummary.join(' / ')}
                        {documentState.truncated
                            ? ` / preview of ${documentState.totalChars.toLocaleString()} chars`
                            : ''}
                    </span>
                </div>
                <div className="flex min-w-0 items-center gap-1.5">
                    <div className="relative w-56 min-w-0">
                        <Search className="pointer-events-none absolute top-1/2 left-2 size-3.5 -translate-y-1/2 text-[var(--aa-ink-faint)]" />
                        <Input
                            value={searchQuery}
                            onChange={(event) =>
                                setSearchQuery(event.target.value)
                            }
                            placeholder="Search payload"
                            className="h-7 rounded-[2px] border-[var(--aa-border)] bg-white pl-7 text-xs text-[var(--aa-ink)] shadow-none placeholder:text-[var(--aa-ink-faint)] focus-visible:ring-[var(--aa-focus)]"
                        />
                    </div>
                    <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className={cn(
                            'size-7 rounded-[2px] border border-[var(--aa-border)] bg-white p-0 text-[var(--aa-ink-muted)] hover:bg-[var(--aa-surface-hover)] hover:text-[var(--aa-ink)]',
                            wrapLines &&
                                'bg-[var(--aa-surface-sunk)] text-[var(--aa-ink)]',
                        )}
                        onClick={() => setWrapLines((current) => !current)}
                        aria-pressed={wrapLines}
                        aria-label="Toggle line wrapping"
                        title="Toggle line wrapping"
                    >
                        <WrapText className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="size-7 rounded-[2px] border border-[var(--aa-border)] bg-white p-0 text-[var(--aa-ink-muted)] hover:bg-[var(--aa-surface-hover)] hover:text-[var(--aa-ink)]"
                        onClick={copyPayload}
                        aria-label={`Copy ${title}`}
                        title="Copy payload"
                    >
                        <ClipboardCopy className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="size-7 rounded-[2px] border border-[var(--aa-border)] bg-white p-0 text-[var(--aa-ink-muted)] hover:bg-[var(--aa-surface-hover)] hover:text-[var(--aa-ink)]"
                        onClick={downloadPayload}
                        aria-label={`Download ${title}`}
                        title="Download payload"
                    >
                        <Download className="h-3.5 w-3.5" />
                    </Button>
                </div>
            </div>
            <div className="min-h-[16rem] flex-1 overflow-auto bg-white font-mono text-[11px] leading-5 text-[var(--aa-ink-soft)]">
                <div
                    className={cn(
                        'grid min-w-max grid-cols-[2.75rem_minmax(0,1fr)]',
                        wrapLines && 'min-w-0',
                    )}
                >
                    {lines.map((line, index) => (
                        <div
                            key={`${index}-${line.slice(0, 16)}`}
                            className="contents"
                        >
                            <div className="border-r border-[var(--aa-border)] bg-[var(--aa-surface)] px-2 text-right text-[var(--aa-ink-faint)] tabular-nums select-none">
                                {index + 1}
                            </div>
                            <pre
                                className={cn(
                                    'min-h-5 px-2.5',
                                    wrapLines
                                        ? 'break-words whitespace-pre-wrap'
                                        : 'whitespace-pre',
                                )}
                            >
                                <code>
                                    {renderPayloadLine(line, searchQuery)}
                                </code>
                            </pre>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
